import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ticketBoardFilters, type TicketBoardFilter } from '@brimo/shared';
import { Button, Skeleton } from '../../ds/components';
import { usePermissions } from '../../lib/session';
import { LockedPage } from '../../shell/ModulePages';
import { useTicketBoard } from './api';
import { ExceededTable } from './board/ExceededTable';
import { FilterBar } from './board/FilterBar';
import { TicketBoard } from './board/TicketBoard';
import { CreateTicketDialog } from './create/CreateTicketDialog';
import type { BoardFilters } from './types';
import { LoadError, isForbidden } from './ui/LoadError';
import { TicketsHeader } from './ui/TicketsHeader';
import page from './ui/page.module.css';

export interface TicketsPageProps {
  /** `/tickets/new`: „Ticket anlegen" is open above the board. */
  createOpen?: boolean | undefined;
}

function isBoardFilter(value: string | null): value is TicketBoardFilter {
  return (ticketBoardFilters as readonly string[]).includes(value ?? '');
}

/**
 * `/tickets` — Backoffice 03 (board) and 04 (`?filter=exceeded`, the escalation table). Filters live in the URL:
 * `?filter=today|exceeded|unassigned`, `?categoryId=`, `?assigneeId=`, `?q=`. `/tickets/new` opens the create dialog.
 */
export function TicketsPage({ createOpen = false }: TicketsPageProps) {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [params, setParams] = useSearchParams();
  const filterParam = params.get('filter');
  const filters: BoardFilters = {
    filter: isBoardFilter(filterParam) ? filterParam : 'all',
    categoryId: params.get('categoryId'),
    assigneeId: params.get('assigneeId'),
    q: params.get('q') ?? '',
  };
  const board = useTicketBoard(filters);

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const closeCreate = useCallback(
    (createdId?: string) => {
      if (createdId) {
        void navigate(`/tickets/${createdId}`);
        return;
      }
      void navigate({ pathname: '/tickets', search: params.toString() ? `?${params.toString()}` : '' });
    },
    [navigate, params],
  );

  if (permissions.tool('tickets') !== 'ok' || isForbidden(board.error)) return <LockedPage tool="tickets" />;

  const data = board.data;
  return (
    <div className={page.page}>
      <TicketsHeader
        eyebrow="Backoffice"
        title={data?.title ?? 'Tickets'}
        line={data?.line}
        tab="board"
        actions={
          <Button
            onClick={() => void navigate(`/tickets/new${params.toString() ? `?${params.toString()}` : ''}`)}
          >
            Ticket anlegen
          </Button>
        }
      />

      {board.isError ? (
        <LoadError
          what="Tickets konnten nicht geladen werden"
          error={board.error}
          retrying={board.isFetching}
          onRetry={() => void board.refetch()}
        />
      ) : null}

      {board.isPending ? (
        <div className={page.skeleton} aria-busy="true">
          <Skeleton width={560} height={30} />
          <Skeleton variant="block" height={420} />
        </div>
      ) : null}

      {data ? (
        <>
          <FilterBar
            board={data}
            categoryId={filters.categoryId}
            assigneeId={filters.assigneeId}
            onFilter={(key) => patch({ filter: key === 'all' ? null : key })}
            onCategory={(id) => patch({ categoryId: id })}
            onAssignee={(id) => patch({ assigneeId: id })}
          />
          {data.exceeded ? (
            <ExceededTable exceeded={data.exceeded} />
          ) : (
            <TicketBoard board={data} dimmed={board.isPlaceholderData && board.isFetching} />
          )}
        </>
      ) : null}

      {createOpen ? <CreateTicketDialog categories={data?.categories ?? []} onClose={closeCreate} /> : null}
    </div>
  );
}
